/**
 * 向听数（shanten）计算
 *
 * 向听数 = 距离听牌还差几张有效牌
 *   -1 = 已和了
 *    0 = 听牌
 *    n = n 向听
 *
 * 三种牌形分别计算，取最小值：
 *   1. 普通形：4 面子 + 1 雀头
 *   2. 七对子：7 个对子
 *   3. 国士无双：13 种幺九牌各 1 张 + 其中 1 种 2 张
 */

import {
  Counts,
  cloneCounts,
  countsTotal,
  hasOverFour,
  isHonor,
  NUM_TILE_TYPES,
} from './tile.js';

/** 向听数结果 */
export interface ShantenResult {
  /** 普通形（4 面子 1 雀头）向听数 */
  readonly regular: number;
  /** 七对子向听数 */
  readonly chiitoitsu: number;
  /** 国士无双向听数 */
  readonly kokushi: number;
  /** 最终向听数（三者最小值） */
  readonly shanten: number;
}

/** 无解 / 非法手牌的向听数 */
export const SHANTEN_INVALID = 8;

/** 国士无双需要的 13 种幺九牌索引 */
const KOKUSHI_INDICES: readonly number[] = [
  0, 8, // 1m 9m
  9, 17, // 1p 9p
  18, 26, // 1s 9s
  27, 28, 29, 30, // 东南西北
  31, 32, 33, // 白发中
];

// ════════════════════════════════════════════════════════════════
// 主入口
// ════════════════════════════════════════════════════════════════

/**
 * 计算向听数
 *
 * @param counts 手牌计数（34 长度）。**不含副露的牌**
 * @param meldCount 已副露的面子数（0-4）。副露的面子视作已完成
 * @returns 三种牌形 + 最小值
 */
export function calculateShanten(counts: Counts, meldCount = 0): ShantenResult {
  // 非法手牌（某种牌 > 4 张）
  if (hasOverFour(counts)) {
    return {
      regular: SHANTEN_INVALID,
      chiitoitsu: SHANTEN_INVALID,
      kokushi: SHANTEN_INVALID,
      shanten: SHANTEN_INVALID,
    };
  }

  // 非法手牌（总牌数不是 13 或 14）—— 只有 0 张（空手牌）例外，用于内部计算
  const total = countsTotal(counts) + meldCount * 3;
  if (total !== 0 && total !== 13 && total !== 14) {
    return {
      regular: SHANTEN_INVALID,
      chiitoitsu: SHANTEN_INVALID,
      kokushi: SHANTEN_INVALID,
      shanten: SHANTEN_INVALID,
    };
  }

  const regular = regularShanten(counts, meldCount);
  // 七对子 / 国士 必须门清（无副露）
  const chiitoitsu = meldCount === 0 ? chiitoitsuShanten(counts) : SHANTEN_INVALID;
  const kokushi = meldCount === 0 ? kokushiShanten(counts) : SHANTEN_INVALID;

  return {
    regular,
    chiitoitsu,
    kokushi,
    shanten: Math.min(regular, chiitoitsu, kokushi),
  };
}

/** 只算最终向听数（性能优化的常用入口） */
export function shanten(counts: Counts, meldCount = 0): number {
  return calculateShanten(counts, meldCount).shanten;
}

// ════════════════════════════════════════════════════════════════
// 普通形（4 面子 + 1 雀头）
// ════════════════════════════════════════════════════════════════

/**
 * 按花色独立搜索：返回该花色能形成的 (面子数, 搭子数) 的所有可能组合
 *
 * 关键洞察：面子/搭子只会在同一花色内形成（顺子需要连续同花色，刻子同种）。
 * 所以可以把 34 种牌拆成 4 个花色，各自独立枚举 (mentsu, tatsu) 组合，
 * 再在花色间做笛卡尔积并汇总 —— 复杂度从「全局分支爆炸」降到「每花色小规模枚举」。
 *
 * @param counts 该花色的计数（9 或 7 长度）
 * @param len 花色长度
 * @param needMentsu 还需要的面子数（用于剪枝）
 */
const comboCache = new Map<string, Array<{ mentsu: number; tatsu: number }>>();

function suitCombinations(
  counts: number[],
  len: number,
  needMentsu: number,
  isHonorSuit = false,
): Array<{ mentsu: number; tatsu: number }> {
  // ⚠️ cacheKey 必须含 isHonorSuit（同长度不同花色语义不同）
  const cacheKey = `${counts.join('')}|${needMentsu}|${isHonorSuit ? 'z' : 'n'}`;
  const cached = comboCache.get(cacheKey);
  if (cached) return cached;

  const results: Array<{ mentsu: number; tatsu: number }> = [];
  const seen = new Set<number>();

  const walk = (i: number, mentsu: number, tatsu: number) => {
    // 剪枝：面子/搭子数超过需求无意义
    const cap = needMentsu + 2;
    if (mentsu > cap || tatsu > needMentsu + 2) return;

    while (i < len && counts[i] === 0) i++;

    if (i >= len) {
      const key = mentsu * 16 + tatsu;
      if (!seen.has(key)) {
        seen.add(key);
        results.push({ mentsu, tatsu });
      }
      return;
    }

    const num = i % 9;
    // ⚠️ 字牌花色不能形成顺子！
    //    字牌切片长度 7，内部索引 0-6，num = i%9 会得到 0..6，
    //    导致「东南西」(idx 0,1,2) 被误判成连续顺子。
    //    （踩过：34m567m234p55s123z 被算成 0 向听，实际是 2 向听）
    const noShuntsu = isHonorSuit;
    // 刻子
    if (counts[i] >= 3) {
      counts[i] -= 3;
      walk(i, mentsu + 1, tatsu);
      counts[i] += 3;
    }
    // 顺子
    if (!noShuntsu && num <= 6 && counts[i + 1] > 0 && counts[i + 2] > 0) {
      counts[i]--; counts[i + 1]--; counts[i + 2]--;
      walk(i, mentsu + 1, tatsu);
      counts[i]++; counts[i + 1]++; counts[i + 2]++;
    }
    // 对子作搭子
    if (counts[i] >= 2) {
      counts[i] -= 2;
      walk(i, mentsu, tatsu + 1);
      counts[i] += 2;
    }
    // 连续搭子（差 1）
    if (!noShuntsu && num <= 7 && counts[i + 1] > 0) {
      counts[i]--; counts[i + 1]--;
      walk(i, mentsu, tatsu + 1);
      counts[i]++; counts[i + 1]++;
    }
    // 坎张（差 2）
    if (!noShuntsu && num <= 6 && counts[i + 2] > 0) {
      counts[i]--; counts[i + 2]--;
      walk(i, mentsu, tatsu + 1);
      counts[i]++; counts[i + 2]++;
    }
    // 孤张
    counts[i]--;
    walk(i, mentsu, tatsu);
    counts[i]++;
  };

  walk(0, 0, 0);
  comboCache.set(cacheKey, results);
  return results;
}

/**
 * 普通形向听数（按花色分解 + 跨花色汇总）
 *
 * 流程：
 *   1. 拆成 4 个花色
 *   2. 每个花色枚举所有 (mentsu, tatsu) 组合
 *   3. 跨花色笛卡尔积，汇总 mentsu/tatsu
 *   4. 对每种汇总算向听数，取最小
 *   5. 雀头已在花色内部作为「对子搭子」处理；额外枚举「某对子作雀头」的情形
 *
 * 正确性要点：雀头必须在某花色内是完整对子。做法是：
 *   · 情形 A：不指定雀头（hasPair=false），用 pairPenalty=1
 *   · 情形 B：把某个对子预留作雀头（hasPair=true），该花色内去掉这 2 张后再枚举
 */
function regularShanten(counts: Counts, meldCount: number): number {
  let best = SHANTEN_INVALID;
  const needMentsu = 4 - meldCount;
  if (needMentsu < 0) return -1;

  /**
   * 手牌总数（含副露占用）。用于区分「已完成(14张)」和「听牌(13张)」：
   * tilesNeeded === 0 表示面子+雀头都齐了，但只有 14 张才是真和牌。
   * 13 张时应为听牌（0 向听），而非已和（-1）。
   */
  const handTotal = countsTotal(counts) + meldCount * 3;

  // 花色切片
  const suits: Array<{ start: number; len: number }> = [
    { start: 0, len: 9 }, // m
    { start: 9, len: 9 }, // p
    { start: 18, len: 9 }, // s
    { start: 27, len: 7 }, // z
  ];

  const suitBuf = (start: number, len: number) => counts.slice(start, start + len);

  // 预计算各花色的组合（情形 A：不拆雀头）
  const combosA = suits.map((s) =>
    suitCombinations(suitBuf(s.start, s.len), s.len, needMentsu, s.start === 27),
  );

  // 汇总（情形 A）
  const traverse = (idx: number, mentsu: number, tatsu: number) => {
    if (mentsu > 4) return;
    if (idx === suits.length) {
      const need = 4 - meldCount - mentsu;
      if (need < 0) {
        // 面子数已超过 4 个（不可能），按已和/听牌处理
        const v = handTotal >= 14 ? -1 : 0;
        if (v < best) best = v;
        return;
      }
      const usable = Math.min(tatsu, need);
      const tilesNeeded = need * 2 - usable + 1; // 无雀头 → +1
      const v = tilesNeeded <= 0 ? (handTotal >= 14 ? -1 : 0) : tilesNeeded - 1;
      if (v < best) best = v;
      return;
    }
    for (const combo of combosA[idx]) {
      traverse(idx + 1, mentsu + combo.mentsu, tatsu + combo.tatsu);
    }
  };
  traverse(0, 0, 0);

  // 情形 B：枚举某个对子作雀头
  for (let t = 0; t < NUM_TILE_TYPES; t++) {
    if (counts[t] < 2) continue;
    const reduced = cloneCounts(counts);
    reduced[t] -= 2;
    const combosB = suits.map((s) =>
      suitCombinations(
        reduced.slice(s.start, s.start + s.len),
        s.len,
        needMentsu,
        s.start === 27,
      ),
    );
    const walkB = (idx: number, mentsu: number, tatsu: number) => {
      if (mentsu > 4) return;
      if (idx === suits.length) {
        const need = 4 - meldCount - mentsu;
        if (need < 0) {
          const v = handTotal >= 14 ? -1 : 0;
          if (v < best) best = v;
          return;
        }
        const usable = Math.min(tatsu, need);
        const tilesNeeded = need * 2 - usable; // 有雀头
        const v = tilesNeeded <= 0 ? (handTotal >= 14 ? -1 : 0) : tilesNeeded - 1;
        if (v < best) best = v;
        return;
      }
      for (const combo of combosB[idx]) {
        walkB(idx + 1, mentsu + combo.mentsu, tatsu + combo.tatsu);
      }
    };
    walkB(0, 0, 0);
    if (best === -1) return -1;
  }

  return best;
}

// ════════════════════════════════════════════════════════════════
// 七对子
// ════════════════════════════════════════════════════════════════

/**
 * 七对子向听数
 *
 * 七对子的限制：
 *   · 必须门清（无副露）
 *   · 必须 7 种**不同**的对子（4 张同牌算 2 对，不允许）
 *
 * 公式：6 - 对子数 + max(0, 7 - 不同种类数)
 *   · 前半：还差几个对子
 *   · 后半：种类不够时的惩罚（需要摸到新种类）
 */
function chiitoitsuShanten(counts: Counts): number {
  let pairs = 0;
  let kinds = 0;
  for (let i = 0; i < NUM_TILE_TYPES; i++) {
    const c = counts[i] ?? 0;
    if (c >= 2) pairs++;
    if (c >= 1) kinds++;
  }
  return 6 - pairs + Math.max(0, 7 - kinds);
}

// ════════════════════════════════════════════════════════════════
// 国士无双
// ════════════════════════════════════════════════════════════════

/**
 * 国士无双向听数
 *
 * 国士无双的限制：
 *   · 必须门清
 *   · 13 种幺九牌各至少 1 张，其中 1 种 2 张（作雀头）
 *
 * 公式：13 - 已有种类数 - (有对子 ? 1 : 0)
 */
function kokushiShanten(counts: Counts): number {
  let kinds = 0;
  let hasPair = false;
  for (const idx of KOKUSHI_INDICES) {
    const c = counts[idx] ?? 0;
    if (c >= 1) kinds++;
    if (c >= 2) hasPair = true;
  }
  return 13 - kinds - (hasPair ? 1 : 0);
}

// ════════════════════════════════════════════════════════════════
// 辅助
// ════════════════════════════════════════════════════════════════

/** 是否听牌（0 向听或已和） */
export function isTenpai(counts: Counts, meldCount = 0): boolean {
  return shanten(counts, meldCount) <= 0;
}

/** 是否已和了牌型（-1 向听） */
export function isAgari(counts: Counts, meldCount = 0): boolean {
  return shanten(counts, meldCount) === -1;
}

/** 手牌张数校验（含副露的面子占 3 张） */
export function validateHandSize(counts: Counts, meldCount: number): boolean {
  const total = countsTotal(counts) + meldCount * 3;
  // 13 张（摸牌前）或 14 张（摸牌后）
  return total === 13 || total === 14;
}
